import React, { useState } from 'react';
import { X, Plus, Trash2, Monitor } from 'lucide-react';
import { CustomDisplayConfig } from './hooks/useCustomDisplays';

interface CustomDisplayDialogProps {
    isOpen: boolean;
    onClose: () => void;
    customDisplays: any[];
    onAdd: (config: CustomDisplayConfig) => void;
    onDelete: (id: string) => void;
}

export default function CustomDisplayDialog({
    isOpen,
    onClose,
    customDisplays,
    onAdd,
    onDelete,
}: CustomDisplayDialogProps) {
    const [name, setName] = useState('');
    const [pixelWidth, setPixelWidth] = useState(1920);
    const [pixelHeight, setPixelHeight] = useState(1080);
    const [scale, setScale] = useState(1.5);
    const [showForm, setShowForm] = useState(false);

    // Calculate points based on pixels and scale
    const pointWidth = Math.round(pixelWidth / scale);
    const pointHeight = Math.round(pixelHeight / scale);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            alert('Please enter a display name');
            return;
        }

        const config: CustomDisplayConfig = {
            id: `custom-display-${Date.now()}`,
            name: name.trim(),
            pixels: {
                width: pixelWidth,
                height: pixelHeight,
            },
            scale,
            points: {
                width: pointWidth,
                height: pointHeight,
            },
        };

        onAdd(config);

        // Reset form
        setName('');
        setPixelWidth(1920);
        setPixelHeight(1080);
        setScale(1.5);
        setShowForm(false);
    };

    const handleCancel = () => {
        setName('');
        setPixelWidth(1920);
        setPixelHeight(1080);
        setScale(1.5);
        setShowForm(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Custom Displays
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Existing Custom Displays */}
                    {customDisplays.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                Your Custom Displays
                            </h3>
                            <div className="space-y-2">
                                {customDisplays.map((display) => (
                                    <div
                                        key={display.id}
                                        className="flex items-center justify-between p-3 border border-gray-200 rounded-md bg-gray-50"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Monitor className="w-5 h-5 text-gray-600" />
                                            <div>
                                                <div className="font-medium text-sm text-gray-900">
                                                    {display.name}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {display.pixels.width}×
                                                    {display.pixels.height}px @
                                                    {display.scale}× →{' '}
                                                    {display.points.width}×
                                                    {display.points.height}pt
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => onDelete(display.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Delete display"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add New Display */}
                    {!showForm ? (
                        <button
                            onClick={() => setShowForm(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Add Custom Display
                        </button>
                    ) : (
                        <div className="border border-gray-300 rounded-lg p-4 bg-white">
                            <h3 className="text-sm font-medium text-gray-900 mb-4">
                                New Custom Display
                            </h3>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Display Name */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Display Name
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        placeholder="e.g., 4K Display, Custom Monitor"
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                {/* Pixel Dimensions */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Width (pixels)
                                        </label>
                                        <input
                                            type="number"
                                            value={pixelWidth}
                                            onChange={(e) =>
                                                setPixelWidth(
                                                    parseInt(e.target.value) ||
                                                        0,
                                                )
                                            }
                                            min="1"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Height (pixels)
                                        </label>
                                        <input
                                            type="number"
                                            value={pixelHeight}
                                            onChange={(e) =>
                                                setPixelHeight(
                                                    parseInt(e.target.value) ||
                                                        0,
                                                )
                                            }
                                            min="1"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Pixel Density */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Pixel Density (Scale)
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={scale}
                                            onChange={(e) =>
                                                setScale(
                                                    parseFloat(
                                                        e.target.value,
                                                    ) || 1,
                                                )
                                            }
                                            min="0.5"
                                            max="4"
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setScale(1)}
                                                className={`px-3 py-2 text-xs border rounded transition-colors ${
                                                    scale === 1
                                                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                1×
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setScale(1.5)}
                                                className={`px-3 py-2 text-xs border rounded transition-colors ${
                                                    scale === 1.5
                                                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                1.5×
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setScale(2)}
                                                className={`px-3 py-2 text-xs border rounded transition-colors ${
                                                    scale === 2
                                                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                2×
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setScale(3)}
                                                className={`px-3 py-2 text-xs border rounded transition-colors ${
                                                    scale === 3
                                                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                3×
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">
                                        Common: 1× (standard), 1.5× (high-res),
                                        2× (Retina), 3× (iPhone)
                                    </div>
                                </div>

                                {/* Calculated Points (Read-only) */}
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                    <div className="text-xs font-medium text-blue-900 mb-1">
                                        Calculated Point Dimensions
                                    </div>
                                    <div className="text-sm text-blue-700 font-mono">
                                        {pointWidth} × {pointHeight} points
                                    </div>
                                    <div className="mt-1 text-xs text-blue-600">
                                        These are the logical dimensions used
                                        for layout
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Add Display
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="text-xs text-gray-600">
                        <strong>Tip:</strong> Custom displays are saved locally
                        and will be available across all your experiences.
                    </div>
                </div>
            </div>
        </div>
    );
}
